import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import { SchemaBillTile } from "./SchemaBillTile";
import { SCHEMA_BILL_STRIP_TONES, type SchemaBillStripTone } from "./schemaBillStripTones";

/**
 * Лента със сметки под схемата — две независими, всяка на половин височина
 * (двете заедно заемат мястото на някогашната една):
 * - „Временни маси" (синя) — име извън схемата; клик = избор, двоен клик =
 *   „Добави";
 * - „Нефискализирани маси" (сива) — закритите без бон, точно както ги показва
 *   списъкът; клик = избор (футърът — както в списъка: „Закрий" е повторното
 *   издаване на бон).
 * Празна лента не се показва (родителят).
 */
export const SchemaBillStrip = ({
  tone,
  bills,
  closingBillIds,
  selectedBillId,
  isFaded,
  onSelect,
  onOpen,
}: {
  tone: SchemaBillStripTone;
  bills: BillOut[];
  closingBillIds: ReadonlySet<number>;
  selectedBillId: number | null;
  /** Сметка на друг сервитьор при обща номерация — бледа плочка (кликаема). */
  isFaded: (bill: BillOut) => boolean;
  onSelect: (billId: number) => void;
  /** Двоен клик — „Добави"; без него двойният клик само избира. */
  onOpen?: (billId: number) => void;
}) => {
  const { t } = useTranslation();
  const tones = SCHEMA_BILL_STRIP_TONES[tone];
  const tileRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Избраната плочка се довежда във видимата част на лентата — и когато се
  // появи в нея (напр. закрита без бон, докато е избрана). Не по `bills`:
  // всяко опресняване би върнало ръчно скролнатата лента.
  const selectedInStrip = selectedBillId !== null && bills.some((bill) => bill.id === selectedBillId);
  useEffect(() => {
    if (selectedBillId === null || !selectedInStrip) return;
    tileRefs.current
      .get(selectedBillId)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [selectedBillId, selectedInStrip]);

  return (
    <div className={`flex shrink-0 items-center gap-4 border-t border-dashed px-[18px] py-0.5 ${tones.band}`}>
      {/* Еднаква ширина — плочките на двете ленти започват на едно място */}
      <div className={`w-[196px] shrink-0 border-r pr-4 ${tones.labelDivider}`}>
        <div
          className={`truncate font-pos-mono text-[11px] font-bold uppercase leading-[1.2] tracking-[.1em] ${tones.title}`}
        >
          {t(tones.titleKey)}
        </div>
        <div className={`mt-0.5 truncate text-[11px] font-medium leading-[1.2] ${tones.hint}`}>
          {t(tones.hintKey)}
        </div>
      </div>
      {/* Скролът работи, но лентата му е скрита — като в списъка с маси;
          отстъпите са за пръстена на избраната плочка */}
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto px-1 py-[5px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {bills.map((bill) => (
          <SchemaBillTile
            key={bill.id}
            tone={tone}
            bill={bill}
            selected={selectedBillId === bill.id}
            closing={closingBillIds.has(bill.id)}
            faded={isFaded(bill)}
            onSelect={() => onSelect(bill.id)}
            onOpen={onOpen ? () => onOpen(bill.id) : undefined}
            tileRef={(element) => {
              if (element) tileRefs.current.set(bill.id, element);
              else tileRefs.current.delete(bill.id);
            }}
          />
        ))}
      </div>
    </div>
  );
};
